import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

export interface SignupFormData {
  name: string;
  email: string;
  organizationName: string;
  organizationSlug: string;
  password: string;
  confirmPassword: string;
}

export interface SignupFormProps {
  onSignup: (data: SignupFormData) => Promise<{ error?: Error }>;
  appName?: string;
  websiteUrl?: string;
  className?: string;
}

export function SignupForm({ 
  onSignup, 
  appName = "Next Store",
  websiteUrl,
  className 
}: SignupFormProps) {
  const [formData, setFormData] = useState<SignupFormData>({
    name: '',
    email: '',
    organizationName: '',
    organizationSlug: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const result = await onSignup(formData);

      if (result.error) {
        setError(result.error.message || 'Failed to create account');
        setIsLoading(false);
        return;
      }

      // Success! Redirect to login
      navigate('/login?signup=success');
    } catch (err) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const generateSlug = (text: string) => {
    return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-surface py-12 ${className || ''}`}>
      <div className="max-w-2xl w-full mx-4">
        <Card className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-success rounded-lg mb-4 shadow-sm">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
            </div>
            <h1 className="heading-lg mb-2">
              Create Your Account
            </h1>
            <p className="caption">
              Start your journey with {appName}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-critical-lighter border border-critical-light rounded-md">
              <p className="text-sm text-critical-dark text-center font-medium">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Personal Info */}
            <div className="pb-5 border-b border-border-subdued">
              <h3 className="heading-sm mb-4">Personal Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    placeholder="John Doe"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    placeholder="you@yourstore.com"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Organization Info */}
            <div className="pb-5 border-b border-border-subdued">
              <h3 className="heading-sm mb-4">Organization Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.organizationName}
                    onChange={(e) => {
                      const orgName = e.target.value;
                      setFormData({ 
                        ...formData, 
                        organizationName: orgName,
                        organizationSlug: generateSlug(orgName)
                      });
                    }}
                    className="input-field"
                    placeholder="My Store"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Store Identifier *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.organizationSlug}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      organizationSlug: generateSlug(e.target.value)
                    })}
                    className="input-field"
                    placeholder="my-store"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-text-subdued mt-1">
                    Your store URL: app.nextstore.ai/{formData.organizationSlug || 'your-store'}
                  </p>
                </div>
              </div>
            </div>

            {/* Password */}
            <div>
              <h3 className="heading-sm mb-4">Security</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-field"
                    placeholder="Min. 8 characters"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="input-field"
                    placeholder="Confirm your password"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Terms */}
          <div className="mt-6 pt-6 border-t border-border-subdued">
            <p className="text-xs text-text-subdued text-center">
              By creating an account, you agree to our{' '}
              <a href="#" className="text-primary hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </p>
          </div>

          {/* Login Link */}
          <div className="mt-4 text-center">
            <p className="text-sm text-text-subdued">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </Card>

        {/* Back to website */}
        {websiteUrl && (
          <div className="mt-6 text-center">
            <a 
              href={websiteUrl}
              className="text-sm text-text-subdued hover:text-text"
            >
              ← Back to website
            </a>
          </div>
        )}
      </div>
    </div>
  );
}













